/**
 * Черновик визарда восстановления: что человек не должен выбирать дважды.
 *
 * Отдельным файлом — чтобы правило проверялось без экрана: полная
 * перезагрузка стирала пять решений подряд, ни одно из которых не устарело.
 *
 * Правило «когда место запрещает запуск» лежит рядом, в
 * `shared/domain/restore-fit.ts`: его обязаны одинаково знать и экран, и
 * сервер.
 */

/**
 * Ключ один на весь визард: пять решений принимаются вместе и порознь теряют
 * смысл — снимок без сайта не восстановишь.
 */
export const DRAFT_KEY = 'ebk.restore.draft';

export interface RestoreDraft {
  site: { hostId: string; siteId: string } | null;
  snapshotId: string | null;
  target: string;
  into: string;
  sources: string[];
  mode: 'resume' | 'mirror' | 'prune';
  keep: string[];
}

/**
 * Прочитать одно поле черновика.
 *
 * Защищено от всего сразу: приватное окно, запрещённая память, чужая версия
 * черновика от прежней разметки. Испорченный черновик — неудобство, а упавший
 * экран восстановления — беда; менять второе на первое нельзя.
 */
export function draft<T>(field: keyof RestoreDraft, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return fallback;
    const value = (parsed as Record<string, unknown>)[field];
    return value === undefined || value === null ? fallback : value as T;
  } catch {
    return fallback;
  }
}

/** Запомнить черновик. Память браузера может быть занята или запрещена — это
 *  удобство, и ронять из-за него работу незачем. */
export function keepDraft(value: Partial<RestoreDraft>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(value));
  } catch { /* см. выше */ }
}
